import React, { useState, useMemo, useCallback } from "react";
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  Button,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
  TablePagination,
  TableCell,
  TableSortLabel,
  TableBody, // Add TableBody import
  TableRow, // Add TableRow import
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useQuery } from "@apollo/client";
import { midgardClients, ADL_RULES_QUERY } from "providers/MidgardProvider";
import { convertToCSV, downloadCSV } from "./configurationPageUtils";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import TagIcon from "@mui/icons-material/Tag";
import RuleIcon from "@mui/icons-material/Rule";
import SettingsIcon from "@mui/icons-material/Settings";
import { CustomJsonEditor } from "components/Nav/Drawers/RightDrawer/FlightDebugger/debuggerComponents/CustomJsonEditor";
import useWindowSize from "utils/customHooks/useWindowSize";

// Shared component for highlighted text search results
export const HighlightedText = ({
  text,
  highlight,
}: {
  text: string;
  highlight: string;
}) => {
  if (!highlight.trim()) {
    return <span>{text}</span>;
  }

  const regex = new RegExp(
    `(${highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi"
  );
  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <span key={i} style={{ backgroundColor: "yellow" }}>
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};

// Specialized component to render ADL rule details in a human-readable format (no JSON)
const HumanReadableRuleDetail = ({ rule }: { rule: any }) => {
  if (!rule) {
    return null;
  }

  return (
    <Box sx={{ p: 2, bgcolor: "#fafafa" }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, color: "#333" }}>
          Rule Summary
        </Typography>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "0 8px",
          }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  fontWeight: 500,
                  color: "#555",
                  width: "150px",
                  verticalAlign: "top",
                }}
              >
                Rule Name:
              </td>
              <td>
                <strong>{rule.ruleName || rule.name || "Unnamed Rule"}</strong>
              </td>
            </tr>
            <tr>
              <td
                style={{ fontWeight: 500, color: "#555", verticalAlign: "top" }}
              >
                Datasource Type:
              </td>
              <td>{rule.datasourceType || "Unknown"}</td>
            </tr>
            {rule.description && (
              <tr>
                <td
                  style={{
                    fontWeight: 500,
                    color: "#555",
                    verticalAlign: "top",
                  }}
                >
                  Description:
                </td>
                <td>{rule.description}</td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
        }}
      >
        {/* CONDITIONS SECTION */}
        <Box
          sx={{
            p: 2,
            border: "1px solid rgba(3, 169, 244, 0.2)",
            borderRadius: 1,
            bgcolor: "rgba(3, 169, 244, 0.03)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <RuleIcon color="info" />
            <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600 }}>
              Conditions
            </Typography>
          </Box>

          {!rule.conditions ? (
            <Typography color="text.secondary">
              No conditions defined
            </Typography>
          ) : (
            <Box>
              {Array.isArray(rule.conditions) ? (
                rule.conditions.map((condition, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      mb: 2,
                      pb: 2,
                      borderBottom:
                        idx < rule.conditions.length - 1
                          ? "1px solid #eee"
                          : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        mb: 1,
                        p: 1,
                        bgcolor: "rgba(3, 169, 244, 0.1)",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ marginRight: "8px", fontWeight: "bold" }}>
                        {idx + 1}.
                      </span>
                      {condition.type
                        ? condition.type.replace(/-/g, " ")
                        : "Filter Condition"}
                    </Typography>

                    {renderConditionDetails(condition)}
                  </Box>
                ))
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      mb: 1,
                      p: 1,
                      bgcolor: "rgba(3, 169, 244, 0.1)",
                      borderRadius: 1,
                    }}
                  >
                    {rule.conditions.type
                      ? rule.conditions.type.replace(/-/g, " ")
                      : "Filter Condition"}
                  </Typography>

                  {renderConditionDetails(rule.conditions)}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* ACTIONS SECTION */}
        <Box
          sx={{
            p: 2,
            border: "1px solid rgba(156, 39, 176, 0.2)",
            borderRadius: 1,
            bgcolor: "rgba(156, 39, 176, 0.03)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <SettingsIcon color="secondary" />
            <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600 }}>
              Actions
            </Typography>
          </Box>

          {!rule.actions ? (
            <Typography color="text.secondary">No actions defined</Typography>
          ) : (
            <Box>
              {Array.isArray(rule.actions) ? (
                rule.actions.map((action, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      mb: 2,
                      pb: 2,
                      borderBottom:
                        idx < rule.actions.length - 1
                          ? "1px solid #eee"
                          : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        mb: 1,
                        p: 1,
                        bgcolor: "rgba(156, 39, 176, 0.1)",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ marginRight: "8px", fontWeight: "bold" }}>
                        {idx + 1}.
                      </span>
                      {action.type ? `${action.type}` : "Autotag Action"}
                    </Typography>

                    {renderActionDetails(action)}
                  </Box>
                ))
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      mb: 1,
                      p: 1,
                      bgcolor: "rgba(156, 39, 176, 0.1)",
                      borderRadius: 1,
                    }}
                  >
                    {rule.actions.type
                      ? `${rule.actions.type}`
                      : "Autotag Action"}
                  </Typography>

                  {renderActionDetails(rule.actions)}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* VARIABLES SECTION */}
        <Box
          sx={{
            p: 2,
            border: "1px solid rgba(63, 81, 181, 0.2)",
            borderRadius: 1,
            bgcolor: "rgba(63, 81, 181, 0.03)",
            gridColumn: { xs: "1", md: "1 / span 2" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <TagIcon color="primary" />
            <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600 }}>
              Variables
            </Typography>
          </Box>

          {!rule.variables ? (
            <Typography color="text.secondary">No variables defined</Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              {Array.isArray(rule.variables)
                ? rule.variables.map((variable, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        border: "1px solid rgba(63, 81, 181, 0.2)",
                        bgcolor: "#fff",
                      }}
                    >
                      <Box sx={{ mb: 1 }}>
                        {Object.keys(variable).map(key => (
                          <Box key={key}>
                            <Chip
                              size="small"
                              label={key}
                              color="primary"
                              sx={{ mb: 1 }}
                            />
                            <Box sx={{ pl: 2 }}>
                              <Typography variant="body2">
                                {formatVariableValue(variable[key])}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))
                : Object.entries(rule.variables).map(([key, value], idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        border: "1px solid rgba(63, 81, 181, 0.2)",
                        bgcolor: "#fff",
                      }}
                    >
                      <Chip
                        size="small"
                        label={key}
                        color="primary"
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ pl: 2 }}>
                        <Typography variant="body2">
                          {formatVariableValue(value)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Helper function to format variable values in a readable way
const formatVariableValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) {
    return "Not set";
  }
  if (typeof value !== "object") {
    return String(value);
  }

  // If it's an object or array
  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: "0 4px",
      }}
    >
      <tbody>
        {Object.entries(value).map(([k, v], i) => (
          <tr key={i}>
            <td
              style={{
                fontWeight: 500,
                color: "#666",
                paddingRight: "8px",
                verticalAlign: "top",
              }}
            >
              {k}:
            </td>
            <td style={{ verticalAlign: "top" }}>
              {typeof v === "object" ? (
                <pre
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    whiteSpace: "pre-wrap",
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  }}
                >
                  {JSON.stringify(v, null, 2)}
                </pre>
              ) : (
                String(v)
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Helper to render action details in a readable way
const renderActionDetails = (action: any) => {
  if (!action) {
    return null;
  }

  return (
    <Box sx={{ pl: 1 }}>
      {/* Tag Sequence */}
      {action.args?.tagSequence && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, mb: 0.5, color: "#555" }}
          >
            Tag Sequence:
          </Typography>

          <Box sx={{ ml: 2 }}>
            {Array.isArray(action.args.tagSequence) &&
              action.args.tagSequence.map((tag: any, idx: number) => {
                // Join path elements or provide a default
                const joinedPath = tag.path?.join("/") || "N/A";
                // Get duration if available
                const duration = tag.maxDurationMinutes;

                return (
                  // Display each tag path/duration info simply
                  <Box key={idx} sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      • {joinedPath}
                      {duration !== undefined &&
                        ` (Max duration: ${duration} minutes)`}
                    </Typography>
                  </Box>
                );
              })}
          </Box>
        </Box>
      )}

      {/* Duration Settings */}
      {(action.maxDurationMinutes ||
        action.durationNPS ||
        action.durationStartUp) && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, mb: 0.5, color: "#555" }}
          >
            Duration Settings:
          </Typography>

          <Box sx={{ ml: 2 }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: "0 4px",
              }}
            >
              <tbody>
                {action.maxDurationMinutes && (
                  <tr>
                    <td
                      style={{ fontWeight: 500, color: "#666", width: "180px" }}
                    >
                      Max Duration:
                    </td>
                    <td>{action.maxDurationMinutes} minutes</td>
                  </tr>
                )}
                {action.durationNPS && (
                  <tr>
                    <td style={{ fontWeight: 500, color: "#666" }}>
                      NPS Duration:
                    </td>
                    <td>{action.durationNPS} minutes</td>
                  </tr>
                )}
                {action.durationStartUp && (
                  <tr>
                    <td style={{ fontWeight: 500, color: "#666" }}>
                      Startup Duration:
                    </td>
                    <td>{action.durationStartUp} minutes</td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Other Arguments */}
      {action.args &&
        Object.keys(action.args).filter(key => key !== "tagSequence").length >
          0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, mb: 0.5, color: "#555" }}
            >
              Additional Parameters:
            </Typography>

            <Box sx={{ ml: 2 }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: "0 4px",
                }}
              >
                <tbody>
                  {Object.entries(action.args)
                    .filter(([key]) => key !== "tagSequence")
                    .map(([key, value]) => (
                      <tr key={key}>
                        <td
                          style={{
                            fontWeight: 500,
                            color: "#666",
                            verticalAlign: "top",
                            paddingRight: "12px",
                            width: "30%",
                          }}
                        >
                          {key}:
                        </td>
                        <td style={{ verticalAlign: "top" }}>
                          {typeof value === "object" ? (
                            <pre
                              style={{
                                margin: 0,
                                fontSize: "12px",
                                whiteSpace: "pre-wrap",
                                fontFamily:
                                  '"Roboto", "Helvetica", "Arial", sans-serif',
                              }}
                            >
                              {JSON.stringify(value, null, 2)}
                            </pre>
                          ) : (
                            String(value)
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </Box>
          </Box>
        )}
    </Box>
  );
};

// Helper to render condition details in a readable way
const renderConditionDetails = (condition: any) => {
  if (!condition) {
    return null;
  }

  return (
    <Box sx={{ pl: 1 }}>
      {/* Production Segment Filter */}
      {condition.args?.productionSegmentFilter && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, mb: 0.5, color: "#555" }}
          >
            Production Settings:
          </Typography>

          <Box sx={{ ml: 2 }}>
            {/* PNL Categories */}
            {condition.args.productionSegmentFilter.pnlCategoryFilter && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  PNL Categories:
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    mt: 0.5,
                    ml: 2,
                  }}
                >
                  {Array.isArray(
                    condition.args.productionSegmentFilter.pnlCategoryFilter
                  ) &&
                    condition.args.productionSegmentFilter.pnlCategoryFilter.map(
                      (cat: string, idx: number) => (
                        <Chip
                          key={idx}
                          size="small"
                          label={cat}
                          variant="outlined"
                          color="info"
                          sx={{ fontSize: "0.8rem" }}
                        />
                      )
                    )}
                </Box>
              </Box>
            )}

            {/* Other production filter properties */}
            {Object.entries(condition.args.productionSegmentFilter).filter(
              ([key]) => key !== "pnlCategoryFilter"
            ).length > 0 && (
              <Box sx={{ mb: 1 }}>
                {Object.entries(condition.args.productionSegmentFilter)
                  .filter(([key]) => key !== "pnlCategoryFilter")
                  .map(([key, value]) => (
                    <Box
                      key={key}
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        bgcolor: "#fafafa",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#555", mb: 0.5 }}
                      >
                        {key.charAt(0).toUpperCase() +
                          key
                            .slice(1)
                            .replace(/([A-Z])/g, " $1")
                            .trim()}
                        :
                      </Typography>
                      {typeof value === "object" ? (
                        Object.entries(value as Record<string, unknown>).map(
                          ([subKey, subVal], i) => (
                            <Box key={i} sx={{ ml: 2, mb: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{ display: "flex" }}
                              >
                                <span
                                  style={{ fontWeight: 500, minWidth: "120px" }}
                                >
                                  {subKey.charAt(0).toUpperCase() +
                                    subKey
                                      .slice(1)
                                      .replace(/([A-Z])/g, " $1")
                                      .trim()}
                                  :
                                </span>
                                <span>
                                  {typeof subVal === "object"
                                    ? Array.isArray(subVal)
                                      ? subVal.join(", ")
                                      : JSON.stringify(subVal)
                                    : String(subVal)}
                                </span>
                              </Typography>
                            </Box>
                          )
                        )
                      ) : (
                        <Typography variant="body2" sx={{ ml: 2 }}>
                          {String(value)}
                        </Typography>
                      )}
                    </Box>
                  ))}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Shift Segment Filter */}
      {condition.args?.shiftSegmentFilter && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, mb: 0.5, color: "#555" }}
          >
            Shift Settings:
          </Typography>

          <Box sx={{ ml: 2 }}>
            {Object.entries(condition.args.shiftSegmentFilter)
              .filter(([key]) => key !== "workScheduleConfigs")
              .map(([key, value]) => (
                <Box
                  key={key}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    bgcolor: "#fafafa",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#555", mb: 0.5 }}
                  >
                    {key.charAt(0).toUpperCase() +
                      key
                        .slice(1)
                        .replace(/([A-Z])/g, " $1")
                        .trim()}
                    :
                  </Typography>
                  {typeof value === "object" ? (
                    Object.entries(value as Record<string, unknown>).map(
                      ([subKey, subVal], idx) => (
                        <Box key={idx} sx={{ ml: 2, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ display: "flex" }}>
                            <span
                              style={{
                                fontWeight: 500,
                                minWidth: "120px",
                              }}
                            >
                              {subKey.charAt(0).toUpperCase() +
                                subKey
                                  .slice(1)
                                  .replace(/([A-Z])/g, " $1")
                                  .trim()}
                              :
                            </span>
                            <span>
                              {typeof subVal === "object"
                                ? Array.isArray(subVal)
                                  ? subVal.join(", ")
                                  : JSON.stringify(subVal)
                                : String(subVal)}
                            </span>
                          </Typography>
                        </Box>
                      )
                    )
                  ) : (
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      {String(value)}
                    </Typography>
                  )}
                </Box>
              ))}

            {/* Work Schedule Configs */}
            {condition.args.shiftSegmentFilter.workScheduleConfigs && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Work Schedule Details:
                </Typography>

                <Box sx={{ ml: 2 }}>
                  {typeof condition.args.shiftSegmentFilter
                    .workScheduleConfigs === "object" &&
                    Object.entries(
                      condition.args.shiftSegmentFilter.workScheduleConfigs
                    ).map(([key, value]) => (
                      <Box
                        key={key}
                        sx={{
                          p: 1.5,
                          mb: 1.5,
                          border: "1px solid #e0e0e0",
                          borderRadius: "4px",
                          bgcolor: "#fafafa",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, color: "#555", mb: 0.5 }}
                        >
                          {key.charAt(0).toUpperCase() +
                            key
                              .slice(1)
                              .replace(/([A-Z])/g, " $1")
                              .trim()}
                          :
                        </Typography>
                        {typeof value === "object" ? (
                          Object.entries(value as Record<string, unknown>).map(
                            ([subKey, subVal], idx) => (
                              <Box key={idx} sx={{ ml: 2, mb: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ display: "flex" }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 500,
                                      minWidth: "120px",
                                    }}
                                  >
                                    {subKey.charAt(0).toUpperCase() +
                                      subKey
                                        .slice(1)
                                        .replace(/([A-Z])/g, " $1")
                                        .trim()}
                                    :
                                  </span>
                                  <span>
                                    {typeof subVal === "object"
                                      ? Array.isArray(subVal)
                                        ? subVal.join(", ")
                                        : JSON.stringify(subVal)
                                      : String(subVal)}
                                  </span>
                                </Typography>
                              </Box>
                            )
                          )
                        ) : (
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            {String(value)}
                          </Typography>
                        )}
                      </Box>
                    ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Process Filter */}
      {condition.processFilter && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, mb: 0.5, color: "#555" }}
          >
            Process Filter:
          </Typography>

          <Box sx={{ ml: 2 }}>
            <Box
              sx={{
                p: 1.5,
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                bgcolor: "#fafafa",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              {condition.processFilter.processGroupNames && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Process Group Names:
                  </Typography>
                  <Box sx={{ ml: 2 }}>
                    {Array.isArray(
                      condition.processFilter.processGroupNames
                    ) ? (
                      <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
                        {condition.processFilter.processGroupNames.map(
                          (group: string, idx: number) => (
                            <li key={idx}>
                              <Typography variant="body2">{group}</Typography>
                            </li>
                          )
                        )}
                      </ul>
                    ) : (
                      <Typography variant="body2">
                        {String(condition.processFilter.processGroupNames)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Process Group Names from condition.args - either location can be used */}
      {condition.args?.processGroupNames && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, mb: 0.5, color: "#555" }}
          >
            Process Groups:
          </Typography>

          <Box sx={{ ml: 2 }}>
            <Box
              sx={{
                p: 1.5,
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                bgcolor: "#fafafa",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Process Groups:
              </Typography>
              {typeof condition.args.processGroupNames === "object" ? (
                <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
                  {Array.isArray(condition.args.processGroupNames) &&
                    condition.args.processGroupNames.map(
                      (group: string, idx: number) => (
                        <li key={idx}>
                          <Typography variant="body2">{group}</Typography>
                        </li>
                      )
                    )}
                </ul>
              ) : (
                <Typography variant="body2" sx={{ ml: 2 }}>
                  {String(condition.args.processGroupNames)}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Open/Closed Segments */}
      {condition.args?.openOrClosedSegments && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, mb: 0.5, color: "#555" }}
          >
            Segment Status:
          </Typography>

          <Box sx={{ ml: 2 }}>
            <Chip
              size="small"
              label={String(condition.args.openOrClosedSegments)}
              color="default"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

/**
 * ADL Rules section for displaying ADL rule templates
 */
export const AdlRulesSection: React.FC = () => {
  const { translate } = useFlightTranslation();
  const { innerHeight } = useWindowSize();
  const cKey = useCustomerKey();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Enhanced sorting state to support multiple columns
  const [sortColumn, setSortColumn] = useState<string>("ruleName");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    error: queryError,
    loading: queryLoading,
    data,
  } = useQuery(ADL_RULES_QUERY, {
    client: midgardClients.get(cKey),
    fetchPolicy: "network-only",
    onError: err => {
      console.error("Error fetching ADL rule templates:", err);
      setError("Failed to load ADL rule templates. Please try again later.");
      setLoading(false);
    },
    onCompleted: () => {
      setLoading(false);
    },
  });

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  // Enhanced sort handling for any column
  const handleSort = useCallback((column: string) => {
    setSortColumn(prev => {
      // If clicking the same column, toggle direction
      if (prev === column) {
        setSortDirection(prevDir => (prevDir === "asc" ? "desc" : "asc"));
        return column;
      } else {
        // If clicking a new column, default to ascending
        setSortDirection("asc");
        return column;
      }
    });
  }, []);

  const toggleRowExpansion = useCallback((index: number) => {
    setExpandedRows(prev =>
      prev.includes(index)
        ? prev.filter(row => row !== index)
        : [...prev, index]
    );
  }, []);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    setExpandedRows([]);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRows([]);
  };

  const adlRuleTemplates = data?.askrConfig?.adlRuleTemplates || "{}";

  const ruleData = useMemo(() => {
    let result: { rules: Array<any> } = { rules: [] };
    try {
      const parsedData = JSON.parse(adlRuleTemplates);
      if (parsedData && typeof parsedData === "object") {
        if (Array.isArray(parsedData)) {
          result = { rules: parsedData };
        } else if (parsedData.rules && Array.isArray(parsedData.rules)) {
          result = parsedData;
        } else {
          result = { rules: Object.values(parsedData) };
        }
      }
    } catch (e) {
      console.error("Error parsing ADL rule templates:", e);
    }
    return result;
  }, [adlRuleTemplates]);

  const filteredAndSortedRules = useMemo(() => {
    let result = [...(ruleData.rules || [])];

    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      result = result.filter(rule => {
        // Get the display name (including the fallback "Unnamed Rule" if no name exists)
        const displayName = (
          rule.ruleName ||
          rule.name ||
          "Unnamed Rule"
        ).toLowerCase();

        return (
          displayName.includes(lowerSearchTerm) ||
          rule.datasourceType?.toLowerCase().includes(lowerSearchTerm) ||
          JSON.stringify(rule).toLowerCase().includes(lowerSearchTerm)
        );
      });
    }

    // Enhanced sorting logic for multiple columns
    result.sort((a, b) => {
      // Helper function to get metrics for a rule
      const getMetricsForRule = (rule: any) => {
        return {
          variablesCount: Array.isArray(rule.variables)
            ? rule.variables.length
            : rule.variables
              ? Object.keys(rule.variables).length
              : 0,
          actionsCount: Array.isArray(rule.actions)
            ? rule.actions.length
            : rule.actions
              ? 1
              : 0,
          conditionsCount: Array.isArray(rule.conditions)
            ? rule.conditions.length
            : rule.conditions
              ? 1
              : 0,
        };
      };

      // Compare based on the selected column
      if (sortColumn === "ruleName") {
        const nameA = (a.ruleName || a.name || "").toLowerCase();
        const nameB = (b.ruleName || b.name || "").toLowerCase();
        return sortDirection === "asc"
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      } else if (sortColumn === "datasourceType") {
        const typeA = (a.datasourceType || "").toLowerCase();
        const typeB = (b.datasourceType || "").toLowerCase();
        return sortDirection === "asc"
          ? typeA.localeCompare(typeB)
          : typeB.localeCompare(typeA);
      } else if (sortColumn === "variablesCount") {
        const metricsA = getMetricsForRule(a);
        const metricsB = getMetricsForRule(b);
        return sortDirection === "asc"
          ? metricsA.variablesCount - metricsB.variablesCount
          : metricsB.variablesCount - metricsA.variablesCount;
      } else if (sortColumn === "conditionsCount") {
        const metricsA = getMetricsForRule(a);
        const metricsB = getMetricsForRule(b);
        return sortDirection === "asc"
          ? metricsA.conditionsCount - metricsB.conditionsCount
          : metricsB.conditionsCount - metricsA.conditionsCount;
      } else if (sortColumn === "actionsCount") {
        const metricsA = getMetricsForRule(a);
        const metricsB = getMetricsForRule(b);
        return sortDirection === "asc"
          ? metricsA.actionsCount - metricsB.actionsCount
          : metricsB.actionsCount - metricsA.actionsCount;
      }
      return 0;
    });

    return result;
  }, [ruleData.rules, searchTerm, sortColumn, sortDirection]);

  const getRuleMetrics = useCallback((rule: any) => {
    return {
      variablesCount: Array.isArray(rule.variables)
        ? rule.variables.length
        : rule.variables
          ? Object.keys(rule.variables).length
          : 0,
      actionsCount: Array.isArray(rule.actions)
        ? rule.actions.length
        : rule.actions
          ? 1
          : 0,
      conditionsCount: Array.isArray(rule.conditions)
        ? rule.conditions.length
        : rule.conditions
          ? 1
          : 0,
    };
  }, []);

  const handleExportCsv = useCallback(() => {
    const csvData = filteredAndSortedRules.map(rule => {
      const metrics = getRuleMetrics(rule);
      return {
        ruleName: rule.ruleName || rule.name || "Unnamed Rule",
        datasourceType: rule.datasourceType || "Unknown",
        variablesCount: metrics.variablesCount,
        conditionsCount: metrics.conditionsCount,
        actionsCount: metrics.actionsCount,
      };
    });

    const csvContent = convertToCSV(csvData);
    downloadCSV(
      csvContent,
      `adl-rules-${new Date().toISOString().slice(0, 10)}.csv`
    );
  }, [filteredAndSortedRules, getRuleMetrics]);

  if (loading || queryLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
            my: 4,
          }}
        >
          <CircularProgress />
          <Typography variant="body1">
            {translate("Loading ADL Rules...")}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error || queryError) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5">
          {translate("ADL Rules Configuration")}
        </Typography>
        <Typography color="error" sx={{ mt: 2 }}>
          {error || queryError?.message || "An unknown error occurred"}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          sx={{ mt: 2 }}
          onClick={() => window.location.reload()}
        >
          {translate("Retry")}
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <div>
          <Typography variant="h5">
            {translate("ADL Rules Configuration")}
          </Typography>
        </div>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Tooltip title={translate("Export to CSV")}>
            <IconButton
              onClick={handleExportCsv}
              disabled={
                !filteredAndSortedRules || filteredAndSortedRules.length === 0
              }
              color="primary"
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ p: 2, bgcolor: "#fafafa", borderBottom: "1px solid #e0e0e0" }}>
        <TextField
          fullWidth
          placeholder={translate("Search ADL rules...")}
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {filteredAndSortedRules.length === 0 ? (
        <Alert severity="info" sx={{ m: 2 }}>
          {searchTerm
            ? translate("No ADL rules found matching your search.")
            : translate("No ADL rules found.")}
        </Alert>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              userSelect: "text",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#ebebeb" }}>
                {/* RULE NAME HEADER CELL */}
                <TableCell
                  align="left"
                  sortDirection={
                    sortColumn === "ruleName" ? sortDirection : false
                  }
                  sx={theme => ({
                    padding: "12px 12px 12px 44px",
                    // padding: "12px",
                    borderBottom: "1px solid #ddd",
                    fontWeight: "bold",
                    verticalAlign: "middle",
                    width: "350px",
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": {
                      backgroundColor: "#dddddd",
                      "& .MuiTableSortLabel-root": {
                        color: theme.palette.primary.main + " !important",
                      },
                    },
                  })}
                  onClick={() => handleSort("ruleName")}
                >
                  <TableSortLabel
                    active={sortColumn === "ruleName"}
                    direction={
                      sortColumn === "ruleName" ? sortDirection : "asc"
                    }
                    sx={theme => ({
                      "& .MuiTableSortLabel-icon": {
                        color: theme.palette.primary.main + " !important",
                      },
                    })}
                  >
                    {translate("Rule Name")}
                  </TableSortLabel>
                </TableCell>

                {/* DATASOURCE TYPE HEADER CELL */}
                <TableCell
                  align="center"
                  sx={theme => ({
                    padding: "12px",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "160px",
                    fontWeight: "bold",
                    userSelect: "none",
                  })}
                >
                  {translate("Datasource Type")}
                </TableCell>

                {/* VARIABLES HEADER CELL */}
                <TableCell
                  align="center"
                  sx={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "95px",
                    fontWeight: "bold",
                    userSelect: "none",
                  }}
                >
                  {translate("Variables")}
                </TableCell>

                {/* CONDITIONS HEADER CELL */}
                <TableCell
                  align="center"
                  sx={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "95px",
                    fontWeight: "bold",
                    userSelect: "none",
                  }}
                >
                  {translate("Conditions")}
                </TableCell>

                {/* ACTIONS HEADER CELL */}
                <TableCell
                  align="center"
                  sx={{
                    padding: "12px",
                    textAlign: "center",
                    borderBottom: "1px solid #ddd",
                    verticalAlign: "middle",
                    width: "80px",
                    fontWeight: "bold",
                    userSelect: "none",
                  }}
                >
                  {translate("Actions")}
                </TableCell>
              </tr>
            </thead>
            <TableBody>
              {filteredAndSortedRules
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((rule, index) => {
                  const actualIndex = page * rowsPerPage + index;
                  const metrics = getRuleMetrics(rule);
                  const isExpanded = expandedRows.includes(actualIndex);

                  return (
                    <React.Fragment key={`rule-${actualIndex}`}>
                      {/* Use TableRow for the main data row */}
                      <TableRow
                        onClick={() => toggleRowExpansion(actualIndex)}
                        sx={{
                          cursor: "pointer",
                          // Base background color
                          backgroundColor: isExpanded
                            ? "#ebebeb" // Expanded base color
                            : index % 2 === 0
                              ? "#ffffff" // Collapsed alternating
                              : "#fafafa", // Collapsed alternating
                          // Hover background color based on expansion state
                          "&:hover": {
                            backgroundColor: isExpanded ? "#dddddd" : "#ebebeb",
                          },
                        }}
                      >
                        {/* Rule Name Cell - Use TableCell */}
                        <TableCell
                          align="left"
                          sx={{
                            padding: "12px 12px 12px 6px",
                            borderBottom: "1px solid #ddd",
                            verticalAlign: "middle",
                            userSelect: "text",
                            // Make this one bold to match AlertRules
                            fontWeight: "bold",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <IconButton
                              onClick={e => {
                                e.stopPropagation();
                                toggleRowExpansion(actualIndex);
                              }}
                              size="small"
                            >
                              {isExpanded ? (
                                <KeyboardArrowDownIcon />
                              ) : (
                                <KeyboardArrowRightIcon />
                              )}
                            </IconButton>
                            <HighlightedText
                              text={
                                rule.ruleName ||
                                rule.name ||
                                translate("Unnamed Rule")
                              }
                              highlight={searchTerm}
                            />
                          </Box>
                        </TableCell>

                        {/* Datasource Type Cell - Use TableCell */}
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px",
                            borderBottom: "1px solid #ddd",
                            textAlign: "center",
                            verticalAlign: "middle",
                            userSelect: "text",
                          }}
                        >
                          <HighlightedText
                            text={rule.datasourceType || translate("Unknown")}
                            highlight={searchTerm}
                          />
                        </TableCell>

                        {/* Variables Cell - Use TableCell */}
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px",
                            borderBottom: "1px solid #ddd",
                            textAlign: "center",
                            verticalAlign: "middle",
                            userSelect: "text",
                          }}
                        >
                          {metrics.variablesCount}
                        </TableCell>

                        {/* Conditions Cell - Use TableCell */}
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px",
                            borderBottom: "1px solid #ddd",
                            textAlign: "center",
                            verticalAlign: "middle",
                            userSelect: "text",
                          }}
                        >
                          {metrics.conditionsCount}
                        </TableCell>

                        {/* Actions Cell - Use TableCell */}
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px",
                            borderBottom: "1px solid #ddd",
                            textAlign: "center",
                            verticalAlign: "middle",
                            userSelect: "text",
                          }}
                        >
                          {metrics.actionsCount}
                        </TableCell>
                      </TableRow>

                      {/* Expanded Row - Use TableRow and TableCell */}
                      {isExpanded && (
                        <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                          <TableCell
                            colSpan={5} // Span across all columns
                            style={{
                              padding: 0,
                              backgroundColor: "#ebebeb",
                              userSelect: "text",
                              borderBottom: "1px solid #ddd", // Add bottom border like others
                            }}
                          >
                            <HumanReadableRuleDetail rule={rule} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
            </TableBody>
          </table>

          <TablePagination
            component="div"
            count={filteredAndSortedRules.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: "1px solid #e0e0e0",
              ".MuiTablePagination-toolbar": {
                minHeight: "52px",
              },
            }}
          />
        </Box>
      )}

      {process.env.NODE_ENV === "development" && (
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            * JSON viewer below is only visible in development mode
          </Typography>
          <CustomJsonEditor
            key={`json-editor-adl-rules`}
            value={ruleData || {}}
            height={innerHeight ? innerHeight - 300 : 500}
          />
        </Box>
      )}
    </Paper>
  );
};
